import type { IIndexable } from "@common-shared/types/general.types"

import type { AvailableHttpMethod } from "@front-shared/types/general"

import { FRONT_GENERAL_CONFIG } from "../config/general.config"
import { UtilHelper } from "./util.helper"

export class HttpHelper {
    /**
     * Send request to server
     */
    static async sendRequest(
        url: string,
        data: IIndexable = {},
        method: AvailableHttpMethod = "GET",
        appendApiUrl?: string,
        headers = {},
        authorizationHeader?: string,
        errorToastrTimeout = FRONT_GENERAL_CONFIG.defaultToastrTimeout,
        ignoreToastrErrorCodes: number[] = [],
        onError?: (
            message: string,
            timeout?: number,
            withHtml?: boolean,
        ) => void,
        onWarning?: (
            message: string,
            timeout?: number,
            withHtml?: boolean,
        ) => void,
        onLogout?: () => void,
        authorizationHeaderKey?: string,
        withCookies = false,
        onRefreshedAccessTokenReceived?: (refreshedToken: string) => void,
        abortSignal?: AbortSignal,
        authRetryLimit = 10,
        authRetryCount = 0,
    ): Promise<any> {
        method = (method as any).toUpperCase()

        if (appendApiUrl && !url.startsWith("/")) {
            url += "/"
        }

        const requestInit: RequestInit = {
            method,

            ...(withCookies
                ? {
                      credentials: "include",
                  }
                : {}),
        }

        const requestInitHeaders: Record<string, string> = Object.assign(
            {},
            headers,
        )

        if (authorizationHeader) {
            requestInitHeaders[
                authorizationHeaderKey ||
                    FRONT_GENERAL_CONFIG.headers.authorization
            ] = authorizationHeader
        }

        if (method === "GET") {
            url = UtilHelper.appendQueryParams(url, data)
        } else {
            requestInit.body = JSON.stringify(data)

            requestInitHeaders[FRONT_GENERAL_CONFIG.headers.contentType] =
                "application/json"
        }

        requestInit.signal = abortSignal
        requestInit.headers = requestInitHeaders

        let result
        try {
            result = await fetch((appendApiUrl || "") + url, requestInit)
        } catch (e) {
            // Don't show error if it wasn't network, but abort by signal
            if (!abortSignal?.aborted) {
                onError?.("Failed to access API")
            }

            return {}
        }

        let response: IIndexable = {}
        let isBodyParsed = true

        try {
            response = await result.json()
        } catch {
            // Aborting after the headers arrive rejects the body read just like
            // a broken payload does, and an abort is the caller's own doing
            if (abortSignal?.aborted) {
                return {}
            }

            // Not everything that answers us speaks our envelope: proxies return
            // html for 502/504, and a dropped body parses as nothing. Falling
            // through to the error path below reports it as a failed request
            // instead of throwing out of here, where callers never catch it.
            isBodyParsed = false
        }

        if (result.ok && isBodyParsed) {
            if (onRefreshedAccessTokenReceived && response.refreshedToken) {
                onRefreshedAccessTokenReceived(response.refreshedToken)
            }

            return response.data
        }

        if (result.status === 401 && authorizationHeader && onLogout) {
            // Retry on auth error for a few times
            if (authRetryCount < authRetryLimit) {
                await UtilHelper.sleep(1)

                return await HttpHelper.sendRequest(
                    url,
                    data,
                    method,
                    appendApiUrl,
                    headers,
                    authorizationHeader,
                    errorToastrTimeout,
                    ignoreToastrErrorCodes,
                    onError,
                    onWarning,
                    onLogout,
                    authorizationHeaderKey,
                    withCookies,
                    onRefreshedAccessTokenReceived,
                    abortSignal,
                    authRetryLimit,
                    authRetryCount + 1,
                )
            }

            onWarning?.("Your session has expired. Please log in again")

            onLogout()
        } else if (
            // An unreadable body carries no error to show, but callers rely on
            // this toast as their only error feedback, so it still has to say
            // something rather than fail in silence
            (response.error || !isBodyParsed) &&
            !ignoreToastrErrorCodes.includes(result.status)
        ) {
            // We trust to put html into the page from our API
            onError?.(
                response.error || "Request failed",
                errorToastrTimeout,
                true,
            )
        }

        // While currently we don't utilize this anywhere, it's possible that we'd want to return data in an error response at some point.
        // When that happens, we can use this shape to distinguish the error data from the success data.
        // In most cases we will end up returning an empty object here, which is fine.
        // Note for whoever starts using it: a failed request then stops looking
        // empty, so consumers must check `errorData` and not only emptiness.
        return {
            ...(response.data ? { errorData: response.data } : {}),
        }
    }
}
